import api from './api';
import type { Depot, CreateDepotRequest } from '../types';

export const depotsService = {
  async list(filters?: { aoId?: string; societeId?: string; candidatId?: string }): Promise<Depot[]> {
    const params = new URLSearchParams();
    if (filters?.aoId) params.append('aoId', filters.aoId);
    if (filters?.societeId) params.append('societeId', filters.societeId);
    if (filters?.candidatId) params.append('candidatId', filters.candidatId);

    const { data } = await api.get<{ success: boolean; data: { depots: Depot[] } }>(
      `/depots?${params.toString()}`
    );
    return data.data.depots;
  },

  async getAll(): Promise<Depot[]> {
    return this.list();
  },

  async getByAoId(aoId: string): Promise<Depot[]> {
    return this.list({ aoId });
  },

  async getById(id: string): Promise<Depot> {
    const { data } = await api.get<{ success: boolean; data: Depot }>(`/depots/${id}`);
    return data.data;
  },

  async upload(aoId: string, file: File, candidatId?: string, societeId?: string): Promise<Depot> {
    // Sanitize filename: remove special characters, accents, and spaces
    const fileExt = file.name.split('.').pop()?.toLowerCase() || 'pdf';
    const cleanName = file.name
      .replace(/\.[^/.]+$/, '')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]/g, '_')
      .replace(/_+/g, '_')
      .substring(0, 50);
    const sanitizedFile = new File([file], `${Date.now()}-${cleanName}.${fileExt}`, { type: file.type });

    const formData = new FormData();
    formData.append('file', sanitizedFile);
    if (candidatId) formData.append('candidatId', candidatId);
    if (societeId) formData.append('societeId', societeId);

    const { data } = await api.post<{ success: boolean; data: { depot: Depot } }>(
      `/appels-offres/${aoId}/depots`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return data.data.depot;
  },

  async download(id: string): Promise<Blob> {
    const { data } = await api.get<Blob>(`/depots/${id}/download`, {
      responseType: 'blob',
    });
    return data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/depots/${id}`);
  },

  // Extraction de texte
  async extractText(id: string): Promise<{ textContent: string }> {
    const { data } = await api.post<{ success: boolean; data: { textContent: string } }>(
      `/depots/${id}/extract`
    );
    return data.data;
  },

  // Import CV complet avec extraction
  async importCV(
    aoId: string,
    societeId: string,
    file: File,
  ): Promise<{
    depot: Depot;
    candidatName: string;
    extractedText: string;
    ocrUsed: boolean;
    metadata?: {
      tokensIn?: number;
      tokensOut?: number;
      model?: string;
      cost?: number;
    };
  }> {
    // Sanitize filename: remove special characters, accents, and spaces
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'pdf';
    const sanitizedName = file.name
      .replace(/\.[^/.]+$/, '') // Remove extension
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^a-zA-Z0-9]/g, '_') // Replace special chars with underscore
      .replace(/_+/g, '_') // Remove multiple underscores
      .substring(0, 50); // Limit length
    const fileName = `${Date.now()}-${sanitizedName}.${fileExtension}`;
    const sanitizedFile = new File([file], fileName, { type: file.type });

    const formData = new FormData();
    formData.append('file', sanitizedFile);
    formData.append('societeId', societeId);

    const { data } = await api.post<{
      success: boolean;
      data: {
        depot: Depot;
        candidatName: string;
        extractedText: string;
        ocrUsed: boolean;
        metadata?: {
          tokensIn?: number;
          tokensOut?: number;
          model?: string;
          cost?: number;
        };
      };
    }>(`/appels-offres/${aoId}/import-cv`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data.data;
  },
};

export default depotsService;
